import React, { FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Save, X } from 'lucide-react';
import { cn } from '@/lib/core/utils';

interface UnifiedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: string;
  children: React.ReactNode;

  // Form-specific props
  onSubmit?: (e: FormEvent) => void | Promise<void>;
  isFormDialog?: boolean;

  // Generic dialog props
  footer?: React.ReactNode;
  showDefaultFooter?: boolean;
  onConfirm?: () => void | Promise<void>;

  // Common props
  isLoading?: boolean;
  submitText?: string;
  confirmText?: string;
  cancelText?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;

  // Delete functionality
  showDeleteButton?: boolean;
  onDelete?: () => void | Promise<void>;
  deleteText?: string;
}

const sizeClasses = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[500px]',
  lg: 'sm:max-w-[600px]',
  xl: 'sm:max-w-[900px]',
};

export const UnifiedDialog: React.FC<UnifiedDialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,

  // Form-specific
  onSubmit,
  isFormDialog = false,

  // Generic dialog
  footer,
  showDefaultFooter = false,
  onConfirm,

  // Common
  isLoading = false,
  submitText = 'Save',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  size = 'md',
  className,

  // Delete functionality
  showDeleteButton = false,
  onDelete,
  deleteText = 'Delete',
}) => {
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      await onSubmit(e);
    }
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  // Form dialog footer
  const formFooter = (
    <div className="flex justify-between w-full">
      {showDeleteButton && onDelete && (
        <Button
          type="button"
          variant="destructive"
          onClick={onDelete}
          disabled={isLoading}
        >
          {deleteText}
        </Button>
      )}
      <div className={`flex gap-2 ${!showDeleteButton ? 'ml-auto' : ''}`}>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isLoading}
        >
          <X className="mr-2 h-4 w-4" />
          {cancelText}
        </Button>
        <Button type="submit" disabled={isLoading}>
          <Save className="mr-2 h-4 w-4" />
          {isLoading ? 'Saving...' : submitText}
        </Button>
      </div>
    </div>
  );

  // Generic dialog footer
  const genericFooter = (
    <>
      <Button variant="outline" onClick={onClose} disabled={isLoading}>
        {cancelText}
      </Button>
      {onConfirm && (
        <Button onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Loading...' : confirmText}
        </Button>
      )}
    </>
  );

  const dialogContent = isFormDialog ? (
    <form onSubmit={handleSubmit} className="space-y-4">
      {children}
    </form>
  ) : (
    <div className="py-4">{children}</div>
  );

  const finalFooter = footer || (isFormDialog ? formFooter : (showDefaultFooter ? genericFooter : undefined));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(sizeClasses[size], className)}
        aria-describedby={description ? undefined : undefined}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {dialogContent}

        {finalFooter && (
          <DialogFooter>
            {finalFooter}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Convenience wrapper for form dialogs
export const FormDialog: React.FC<Omit<UnifiedDialogProps, 'isFormDialog' | 'onConfirm' | 'showDefaultFooter'> & { onSubmit: (e: FormEvent) => void | Promise<void> }> = (props) => (
  <UnifiedDialog {...props} isFormDialog={true} />
);

// Convenience wrapper for generic dialogs
export const BaseDialog: React.FC<Omit<UnifiedDialogProps, 'isFormDialog' | 'onSubmit'>> = (props) => (
  <UnifiedDialog {...props} isFormDialog={false} />
);